import React, { useEffect, useState } from "react";
import ReactPDF, { PDFViewer } from "@react-pdf/renderer";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getSingleAccountability } from "../../redux/userSlice";
import { Spin } from "../../Components/UI";
import { DownloadOutlined, ArrowLeftOutlined } from "../../Components/UI/Icons";
import {
  Box, Flex, Heading, Text, Image, HStack,
  SimpleGrid, Button, Divider, Table as ChakraTable, Thead, Tbody, Tr, Th, Td
} from "@chakra-ui/react";
import { LOGO_BASE64, BANNER_BASE64 } from "./logoAssets";
import AccountabilityDoc from "./Documents/AccountabilityDoc";
import { getAllTimes } from "../../Utils/auth";
import { recordFileDownloadAudit, recordViewAudit } from "../../Utils/auditLogger";

// ── inline components ─────────────────────────────────────────────────────────
const MetaCard = ({ label, value }) => (
  <Box>
    <Text fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb="2px">{label}</Text>
    <Text fontSize="sm" fontWeight="600" color="brand.text">{value || "—"}</Text>
  </Box>
);

const SectionHeading = ({ children }) => (
  <Flex align="center" justify="space-between" mb={3} mt={6}>
    <Heading size="sm" color="brand.text">{children}</Heading>
  </Flex>
);

// ── Main Report Page ──────────────────────────────────────────────────────────
function AccountabilityReport() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const load = async () => {
      const res = await dispatch(getSingleAccountability(id));
      if (res?.payload?.success) {
        setData(res.payload.data);
        recordViewAudit({
          module: "Accountability Mechanism Report",
          targetId: id,
          details: `Viewed Accountability Mechanism Report (ID: ${id})`,
        });
      }
      setLoading(false);
    };
    load();
  }, [dispatch, id]);

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const blob = await ReactPDF.pdf(<AccountabilityDoc data={data} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const pdfName = `Accountability-Report-${id}.pdf`;
      link.download = pdfName;
      link.click();
      URL.revokeObjectURL(url);

      recordFileDownloadAudit({
        fileName: pdfName,
        fileType: "PDF",
        module: "Accountability Mechanism Report",
        extraDetails: `Report ID: ${id}`,
      });
    } finally {
      setDownloading(false);
    }
  };

  const fmt = (d) => getAllTimes(d)?.formattedDate2 ?? "—";
  const dateRange = data?.fromDate && data?.toDate ? `${fmt(data.fromDate)} – ${fmt(data.toDate)}` : "—";

  return (
    <Box p={{ base: 4, md: 8 }} minH="calc(100vh - 72px)" bg="brand.background">
      <Box maxW="1200px" mx="auto">

        {/* Header */}
        <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={4}>
          <HStack spacing={3}>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<ArrowLeftOutlined />}
              color="gray.500"
              _hover={{ color: "brand.text", bg: "white" }}
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
            <Box>
              <Heading size="lg" color="brand.text">Accountability Mechanism</Heading>
              <Text color="gray.500" fontSize="sm">{data?.formName || "Report"} · {dateRange}</Text>
            </Box>
          </HStack>
          <Button
            leftIcon={<DownloadOutlined />}
            bg="brand.primary"
            color="white"
            _hover={{ bg: "brand.secondary" }}
            onClick={downloadPDF}
            isLoading={downloading}
            loadingText="Generating PDF…"
            size="md"
            isDisabled={loading || !data}
          >
            Download PDF
          </Button>
        </Flex>

        {loading ? (
          <Flex justify="center" align="center" h="400px">
            <Spin size="large" />
          </Flex>
        ) : !data ? (
          <Box textAlign="center" py={20}><Text color="gray.400">Report not found.</Text></Box>
        ) : (
          <>
            {/* Logo banner */}
            <Flex justify="center" align="center" gap={6} bg="white" p={5} borderRadius="2xl" boxShadow="sm" borderWidth="1px" borderColor="gray.100" mb={6}>
              <Image src={LOGO_BASE64} h="70px" w="auto" alt="Logo" />
              <Image src={BANNER_BASE64} h="50px" w="auto" alt="Banner" />
            </Flex>

            {/* Meta info */}
            <Box bg="white" borderRadius="2xl" p={5} boxShadow="sm" borderWidth="1px" borderColor="gray.100" mb={6}>
              <Text fontWeight="700" color="brand.text" mb={3}>Report Details</Text>
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                <MetaCard label="Report Name" value={data.formName} />
                <MetaCard label="Date Range" value={dateRange} />
                <MetaCard label="Observer" value={data.userId?.name} />
                <MetaCard label="Status" value={data.isComplete ? "Completed" : "Draft"} />
              </SimpleGrid>
            </Box>

            {/* Quick Summary View (Non-PDF) */}
            <Box bg="white" borderRadius="2xl" p={5} boxShadow="sm" borderWidth="1px" borderColor="gray.100" mb={6} overflowX="auto">
              <SectionHeading>Teacher Scores Summary</SectionHeading>
              <ChakraTable variant="simple" size="sm">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Teacher</Th>
                    <Th isNumeric>CW</Th>
                    <Th isNumeric>NB</Th>
                    <Th isNumeric>LP</Th>
                    <Th isNumeric>QP</Th>
                    <Th isNumeric>DA</Th>
                    <Th isNumeric>MS</Th>
                    <Th isNumeric>Annual</Th>
                    <Th isNumeric>Micro</Th>
                    <Th isNumeric>Total</Th>
                    <Th isNumeric>Percent</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.teacherScores?.map((ts, idx) => {
                    let total = (ts.classroomWalkthroughAvg || 0) + (ts.notebookCheckingAvg || 0);
                    let maxMarks = 10 + 10;

                    if (!ts.lessonPlanScore_na) { total += ts.lessonPlanScore || 0; maxMarks += 10; }
                    if (!ts.qualityOfQPScore_na) { total += ts.qualityOfQPScore || 0; maxMarks += 10; }

                    const daNA = ts.daAverage_na;
                    if (!daNA) { total += ts.daAverage || 0; maxMarks += 10; }

                    if (!ts.mindspark_na) { total += ts.mindspark || 0; maxMarks += 10; }

                    const annualNA = ts.sec1_na && ts.sec2_na && ts.sec3_na && ts.sec4_na;
                    if (!annualNA) { total += ts.annualReducedTo10 || 0; maxMarks += 10; }

                    if (!ts.microTeaching_na) { total += ts.microTeaching || 0; maxMarks += 20; }

                    const calculatedTotal = parseFloat(total.toFixed(2));
                    const calculatedPct = maxMarks > 0 ? parseFloat(((total / maxMarks) * 100).toFixed(2)) : 0;

                    return (
                      <Tr key={idx}>
                        <Td fontWeight="500">{ts.teacherName}</Td>
                        <Td isNumeric>{ts.classroomWalkthroughAvg || 0}</Td>
                        <Td isNumeric>{ts.notebookCheckingAvg || 0}</Td>
                        <Td isNumeric>{ts.lessonPlanScore_na ? "N/A" : (ts.lessonPlanScore || 0)}</Td>
                        <Td isNumeric>{ts.qualityOfQPScore_na ? "N/A" : (ts.qualityOfQPScore || 0)}</Td>
                        <Td isNumeric>{daNA ? "N/A" : (ts.daAverage || 0)}</Td>
                        <Td isNumeric>{ts.mindspark_na ? "N/A" : (ts.mindspark || 0)}</Td>
                        <Td isNumeric>{annualNA ? "N/A" : (ts.annualReducedTo10 || 0)}</Td>
                        <Td isNumeric>{ts.microTeaching_na ? "N/A" : (ts.microTeaching || 0)}</Td>
                        <Td isNumeric fontWeight="bold" color={calculatedPct < 50 ? "red.500" : "green.500"}>{calculatedTotal}</Td>
                        <Td isNumeric fontWeight="bold">{calculatedPct}%</Td>
                      </Tr>
                    );
                  })}
                  {(!data.teacherScores || data.teacherScores.length === 0) && (
                    <Tr><Td colSpan={11} textAlign="center" color="gray.500">No teacher scores</Td></Tr>
                  )}
                </Tbody>
              </ChakraTable>
            </Box>

            {/* PDF Preview */}
            <Box bg="white" borderRadius="2xl" p={5} boxShadow="sm" borderWidth="1px" borderColor="gray.100">
              <Flex align="center" justify="space-between" mb={4}>
                <Heading size="md" color="brand.text">Live PDF Preview</Heading>
                <Text fontSize="xs" color="gray.400">Scroll to see all pages</Text>
              </Flex>
              <Box borderRadius="xl" overflow="hidden" borderWidth="1px" borderColor="gray.200">
                {data && (
                  <PDFViewer key={id} style={{ width: "100%", height: "800px", border: "none" }}>
                    <AccountabilityDoc data={data} />
                  </PDFViewer>
                )}
              </Box>
            </Box>

          </>
        )}
      </Box>
    </Box>
  );
}

export default AccountabilityReport;
